import { useMemo, useRef, useState } from 'react'
import type { CanvasPosition, CanvasViewport } from '../../types/infrastructure-map'
import type { LinkContextMenuState, NodeContextMenuState } from './canvas'
import type { EndpointRelationship } from '../../types/case-container/relationships'
import type { CaseEndpoint } from '../../types'
import { buildParentChildMap } from './infrastructure-map-canvas.helpers'

const DEFAULT_VIEWPORT: CanvasViewport = {
  x: 0,
  y: 0,
  zoom: 1,
  width: 800,
  height: 600,
}

export interface CanvasState {
  containerRef: React.RefObject<HTMLDivElement | null>
  canvasRef: React.RefObject<HTMLDivElement | null>
  viewport: CanvasViewport
  setViewport: React.Dispatch<React.SetStateAction<CanvasViewport>>
  isPanning: boolean
  setIsPanning: React.Dispatch<React.SetStateAction<boolean>>
  panStart: { x: number; y: number }
  setPanStart: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>
  selectedEndpointIds: Set<string>
  setSelectedEndpointIds: React.Dispatch<React.SetStateAction<Set<string>>>
  lastClickedIdRef: React.MutableRefObject<string | null>
  nodeContextMenu: NodeContextMenuState | null
  setNodeContextMenu: React.Dispatch<React.SetStateAction<NodeContextMenuState | null>>
  detailsEndpointId: string | null
  setDetailsEndpointId: React.Dispatch<React.SetStateAction<string | null>>
  linkContextMenu: LinkContextMenuState | null
  setLinkContextMenu: React.Dispatch<React.SetStateAction<LinkContextMenuState | null>>
  editLinkId: string | null
  setEditLinkId: React.Dispatch<React.SetStateAction<string | null>>
  dragPositions: Map<string, CanvasPosition>
  setDragPositions: React.Dispatch<React.SetStateAction<Map<string, CanvasPosition>>>
}

export interface MapDeps {
  linkedEndpointIds: Set<string>
  endpointIds: string[]
  parentChildMap: Map<string, string[]>
}

export function useMapDeps(
  endpoints: CaseEndpoint[],
  relationships: EndpointRelationship[]
): MapDeps {
  const linkedEndpointIds = useMemo(() => {
    const linked = new Set<string>()
    for (const rel of relationships) {
      linked.add(rel.sourceEndpointId)
      linked.add(rel.targetEndpointId)
    }
    return linked
  }, [relationships])
  const endpointIds = useMemo(() => endpoints.map((e) => e.id), [endpoints])
  const parentChildMap = useMemo(() => buildParentChildMap(relationships), [relationships])
  return { linkedEndpointIds, endpointIds, parentChildMap }
}

export function useCanvasState(): CanvasState {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const lastClickedIdRef = useRef<string | null>(null)
  const [viewport, setViewport] = useState<CanvasViewport>(DEFAULT_VIEWPORT)
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [selectedEndpointIds, setSelectedEndpointIds] = useState<Set<string>>(new Set())
  const [nodeContextMenu, setNodeContextMenu] = useState<NodeContextMenuState | null>(null)
  const [detailsEndpointId, setDetailsEndpointId] = useState<string | null>(null)
  const [linkContextMenu, setLinkContextMenu] = useState<LinkContextMenuState | null>(null)
  const [editLinkId, setEditLinkId] = useState<string | null>(null)
  const [dragPositions, setDragPositions] = useState<Map<string, CanvasPosition>>(new Map())

  return {
    containerRef,
    canvasRef,
    lastClickedIdRef,
    viewport,
    setViewport,
    isPanning,
    setIsPanning,
    panStart,
    setPanStart,
    selectedEndpointIds,
    setSelectedEndpointIds,
    nodeContextMenu,
    setNodeContextMenu,
    detailsEndpointId,
    setDetailsEndpointId,
    linkContextMenu,
    setLinkContextMenu,
    editLinkId,
    setEditLinkId,
    dragPositions,
    setDragPositions,
  }
}
